
import React, { useState, useCallback } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Calculator Form Component
const CalculatorForm = ({ 
  totalAssets, 
  setTotalAssets, 
  zakatRate, 
  setZakatRate, 
  nisabThreshold, 
  setNisabThreshold, 
  handleCalculateZakat, 
  resetFlow 
}) => (
  <div className="font-serif h-96 bg-gradient-to-br from-teal-800 via-teal-900 to-green-900 flex flex-col items-center justify-center px-6 relative overflow-hidden">
    <div 
      className="absolute inset-0 opacity-30"
      style={{
        backgroundImage: `url('/assets/zakat.webp')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    ></div>

    <div className="absolute inset-0 opacity-20">
      <div className="absolute top-10 left-10 text-yellow-400 text-3xl">🌙</div>
      <div className="absolute top-20 right-20 text-yellow-400 text-2xl">⭐</div>
      <div className="absolute bottom-20 left-20 text-yellow-400 text-2xl">✨</div>
      {/* <div className="absolute bottom-10 right-10 text-yellow-400 text-3xl">🕌</div> */}
    </div>

    <div className="text-center relative z-10 font-serif">
      {/* <div className="text-lg ">🧮</div> */}
      <h2 className="text-white text-lg font-bold font-serif ">Zakat Calculator</h2>
      {/* <p className="text-white/90 text-sm">Enter your asset details below</p> */}
    </div>

    <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-2 border border-white/30 max-w-sm w-full relative z-10">
      <div className="space-y-1">
        <div>
          <label className="block text-white text-sm font-medium mb-1 font-serif">Total Assets (IDR)</label>
          <input
            type="number"
            value={totalAssets}
            onChange={(e) => setTotalAssets(e.target.value)}
            className="font-serif w-full px-1 py-1 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            placeholder="Enter your total assets"
          />
        </div>

        <div>
          <label className="font-serif block text-white text-sm font-medium mb-1">Zakat Rate (%)</label>
          <input
            type="number"
            value={zakatRate}
            onChange={(e) => setZakatRate(e.target.value)}
            className="w-full px-1 py-1 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            placeholder="2.5"
          />
        </div>

        <div>
          <label className="font-serif block text-white text-sm font-medium mb-1">Nisab Threshold (IDR)</label>
          <input
            type="number"
            value={nisabThreshold}
            onChange={(e) => setNisabThreshold(e.target.value)}
            className="w-full px-1 py-1 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            placeholder="85000000"
          />
        </div>

        <div className="space-y-1 pt-1">
          <button
            onClick={handleCalculateZakat}
            className=" w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-bold py-1 px-1 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
          >
            Calculate Zakat
          </button>
          
          <button
            onClick={resetFlow}
            className="w-full bg-white/20 border border-white/30 text-white font-medium py-1 px-1 rounded-xl hover:bg-white/30 transition-all duration-200"
          >
            Reset
          </button>
        </div>
      </div>
    </div>

    {/* Maybank Logo */}
    <div className="flex items-center justify-center space-x-1 mt-2 relative z-10">
      
      <div className="flex items-center space-x-0">
            <img
   src="/assets/may1.png" // Replace with your actual image path
   alt="Traveloka Logo"
   className="w-8 h-6 object-contain" // Adjust size as needed
 />
 <h1 className={`text-white font-bold font-serif`}>
 Maybank
 </h1>

</div> 
      {/* <span className="text-white font-bold text-lg">Maybank</span> */}
    </div>
  </div>
);

// Zakat Calculator Component
const ZakatCalculatorFlow = ({ isDesktop = false }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [totalAssets, setTotalAssets] = useState('');
  const [zakatRate, setZakatRate] = useState('2.5');
  const [nisabThreshold, setNisabThreshold] = useState('85000000');
  const [calculatedZakat, setCalculatedZakat] = useState(0);

  const handleClickHere = () => setCurrentStep(2);

  const handleCalculateZakat = useCallback(() => {
    const assets = parseFloat(totalAssets);
    const threshold = parseFloat(nisabThreshold);
    const rate = parseFloat(zakatRate) / 100;
    setCalculatedZakat(assets >= threshold ? assets * rate : 0);
    setCurrentStep(3);
  }, [totalAssets, nisabThreshold, zakatRate]);

  const resetFlow = () => {
    setCurrentStep(1);
    setTotalAssets('');
    setZakatRate('2.5');
    setNisabThreshold('85000000');
    setCalculatedZakat(0);
  };

  const backToCalculator = () => setCurrentStep(2);

  const LandingScreen = () => (
    <div className="font-serif h-96 bg-gradient-to-br from-teal-800 via-teal-900 to-green-900 flex flex-col relative overflow-hidden">
      {/* Background Pattern - Islamic decorative elements */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url('/assets/zakat.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      ></div>

      {/* Header */}
      <div className="relative z-10 pt-1 pb-1 px-6 font-serif">
        <div className="text-center ">
          <h1 className={`text-white font-bold  font-serif${isDesktop ? 'text-lg' : 'text-lg'}`}>Calculate Your Zakat!</h1>
          {/* <div className={`mb-1 ${isDesktop ? 'text-3xl' : 'text-2xl'}`}>🕌</div> */}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 relative z-10">
        <div className={`bg-white/10 backdrop-blur-sm rounded-2xl p-3 border border-white/20 w-full text-center ${isDesktop ? 'max-w-md' : 'max-w-xs'}`}>
          <div className="mb-0 font-serif">
            <h2 className={`text-white font-bold mb-1 font-serif ${isDesktop ? 'text-lg' : 'text-lg'}`}>Fulfill Your Religious Duty this Ramadan</h2>
            <p className={`text-white/90 mb-1 font-serif${isDesktop ? 'text-base' : 'text-sm'}`}>Calculate your Zakat obligation with our easy-to-use calculator</p>
          </div>
          
          {/* Click Here Button */}
          <button 
            onClick={handleClickHere}
            className={`bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-bold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200 w-full mb-6 ${isDesktop ? 'py-1 px-2 text-xl' : 'py-2 px-6 text-lg'}`}
          >
            Click here! 👆
          </button>

          {/* Maybank Logo */}
          <div className="flex items-center justify-center space-x-1">
            {/* <div className="w-8 h-8 bg-yellow-500 rounded flex items-center justify-center">
              <span className="text-black font-bold text-sm">M</span>
            </div> */}

            <div className="flex items-center space-x-0 font-serif">
            <img
   src="/assets/may1.png" // Replace with your actual image path
   alt="Traveloka Logo"
   className="w-8 h-6 object-contain" // Adjust size as needed
 />
 <h1 className={`text-white font-bold ${isDesktop ? 'text-sm font-serif' : 'text-base font-serif'}`}>
 Maybank
 </h1>

</div> 
            {/* <span className={`text-white font-bold ${isDesktop ? 'text-xl' : 'text-lg'}`}>Maybank</span> */}
          </div>
        </div>
      </div>

      {/* Bottom Text */}
      <div className="relative z-10 pb-16 px-6">
        <p className={`text-white/80 text-center ${isDesktop ? 'text-base' : 'text-sm'}`}>
          Barakallahu feeki - May Allah bless your wealth
        </p>
      </div>
    </div>
  );

  const ResultsScreen = () => (
    <div className="h-96 bg-gradient-to-br from-teal-800 via-teal-900 to-green-900 flex flex-col items-center justify-center px-6 relative overflow-hidden">
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url('/assets/zakat.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      ></div>
      
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-10 left-10 text-yellow-400 text-3xl">🌙</div>
        <div className="absolute top-20 right-20 text-yellow-400 text-2xl">⭐</div>
        <div className="absolute bottom-20 left-20 text-yellow-400 text-2xl">✨</div>
        <div className="absolute bottom-10 right-10 text-yellow-400 text-3xl">🕌</div>
      </div>

      {/* Results */}
      <div className="text-center relative z-10 font-serif">
        {/* <div className={`mb-1 animate-pulse mt-1 ${isDesktop ? 'text-2xl' : 'text-2xl'}`}>📊</div> */}
        <h2 className={`text-white font-bold mb-1 ${isDesktop ? 'text-lg' : 'text-lg'}`}>Zakat Calculation Result</h2>
        
        <div className={`bg-white/20 backdrop-blur-sm rounded-2xl p-8 border border-white/30 w-full ${isDesktop ? 'max-w-md' : 'max-w-sm'}`}>
          <div className="space-y-4 font-serif">
            <div className="border-t border-white/30 pt-2">
              <p className={`text-white/90 mb-1 ${isDesktop ? 'text-base' : 'text-sm'}`}>Your Zakat Obligation:</p>
              <p className={`text-yellow-400 font-bold ${isDesktop ? 'text-3xl' : 'text-2xl'}`}>
                IDR {calculatedZakat.toLocaleString()}
              </p>
              {calculatedZakat === 0 && totalAssets && (
                <p className={`text-white/80 mt-2 ${isDesktop ? 'text-sm' : 'text-xs'}`}>
                  Your assets are below the nisab threshold, so no zakat is required.
                </p>
              )}
            </div>
          </div>
          <div className={`mt-2 space-y-1 ${isDesktop ? 'flex space-x-2 space-y-0' : ' font-serif'}`}>
            <button 
              onClick={backToCalculator} 
              className={`bg-white text-black rounded-lg font-medium transition-all duration-200 hover:bg-gray-100 ${isDesktop ? 'px-4 py-1 text-base flex-1' : 'px-4 py-2 w-full text-sm'}`}
            >
              Recalculate
            </button>
            <button 
              onClick={resetFlow} 
              className={`bg-yellow-500 text-black rounded-lg font-medium transition-all duration-200 hover:bg-yellow-400 ${isDesktop ? 'px-6 py-3 text-base flex-1' : 'px-4 py-2 w-full text-sm'}`}
            >
              New Calculation
            </button>
          </div>
        </div>
      </div>

      {/* Maybank Logo */}
      <div className="flex items-center justify-center space-x-2 mt-4 mb-2 relative z-10">
     
    
        <div className="flex items-center space-x-0">
            <img
   src="/assets/may1.png" // Replace with your actual image path
   alt="Traveloka Logo"
   className="w-8 h-6 object-contain" // Adjust size as needed
 />
 <h1 className={`text-white font-bold font-serif${isDesktop ? 'text-sm font-serif' : 'text-base font-serif'}`}>
 Maybank
 </h1>

</div> 
      </div>
    </div>
  );

  return (
    <div className="w-full h-full">
      {currentStep === 1 && <LandingScreen />}
      {currentStep === 2 && (
        <CalculatorForm
          totalAssets={totalAssets}
          setTotalAssets={setTotalAssets}
          zakatRate={zakatRate}
          setZakatRate={setZakatRate}
          nisabThreshold={nisabThreshold}
          setNisabThreshold={setNisabThreshold}
          handleCalculateZakat={handleCalculateZakat}
          resetFlow={resetFlow}
        />
      )}
      {currentStep === 3 && <ResultsScreen />}
    </div>
  );
};

function App() {
  const [isMobile, setIsMobile] = useState(true);

  return (
    <>
      <Helmet>
       {/* <title>
       Bidbox Ad Gallery | Showcase Targeted Campaign Creativity
        </title>
        <meta
          name="description"
          content="Explore Bidbox’s Ad Gallery – see powerful, targeted ad campaigns in action. Get inspired & elevate your advertising with DSP strategy insights."
        /> */}
        {/* <meta
          name="keywords"
          content="BidBox AI, Demand-Side Platform, DSP solutions, targeted advertising, ad campaign optimization, programmatic advertising, real-time bidding, ad tech, audience segmentation, AI-driven advertising, digital marketing, performance marketing, cross-channel advertising, ad exchange, ad placement, ad inventory, ad network, data-driven marketing, media buying."
        /> */}
         <link
          rel="canonical"
          href="https://bidbox.ai/dynamic-calculator/zakat"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
    </Helmet>
    <div className="min-h-screen bg-gray-50 mt-10">
      <div className="container mx-auto px-8 py-8 lg:py-12">
        <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-12">
          {/* Left column - Content */}
          <div className="flex-1 lg:max-w-lg">
            {/* Header */}
            {/* <header className="animate-slide-up">
            
              <div className="mb-4 lg:mt-16 md:mt-10 sm:mt-0">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                  Calculate with Clarity.
                  </h1>
                  <span className="bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  Dynamic Calculator
                  </span>
                </div>
              </div>
            </header> */}
            <header className="animate-slide-up">
  {/* Title and tag */}
  <div className="mb-4 lg:mt-16 md:mt-10 sm:mt-0">
    <div className="flex flex-col items-start gap-3 mb-3">
      <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
        Calculate with Clarity.
      </h1>
      <span className="bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-medium">
        Dynamic Calculator
      </span>
    </div>
  </div>
</header>


            {/* Content Section */}
            <div className="animate-slide-up" style={{ animationDelay: '0.2s' }}>
              {/* Make it yours section */}
              <div className="mb-8">
                <h2 className="text-lg font-bold text-teal-600 mb-4 tracking-wide">
                DRIVE MEANINGFUL ENGAGEMENT
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6 max-w-md">
                This interactive Zakat calculator guides users through a simple process to determine their exact contribution. With customizable rates and an elegant Islamic design, it’s ideal for finance apps, awareness drives, or educational campaigns. Just plug in your branding and launch a tool that informs and empowers.
                </p>
                
                {/* CTA Button */}
                <Link to='/contact'>
                <button className="group bg-white border-2 border-teal-600 text-teal-600 px-6 py-3 rounded-full font-medium hover:bg-teal-600 hover:text-white transition-all duration-300 flex items-center space-x-2 shadow-sm hover:shadow-md transform hover:-translate-y-0.5">
                  <span>Start with this Template</span>
                  <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-200" />
                </button>
                </Link>
              </div>
            </div>
          </div>

          {/* Right column - Template Preview */}
          <div className="flex-1 lg:max-w-2xl mt-8 lg:mt-0">
            {/* Preview toggle */}
            <div className="hidden lg:flex justify-center mt-2 animate-fade-in mb-4" style={{ animationDelay: '0.3s' }}>
                <div className="bg-white rounded-full px-6 py-3 shadow-lg border border-gray-200">
                  <div className="flex items-center space-x-4">
                      {/* <span className="text-sm text-gray-600 font-medium">Preview in:</span> */}
                    <div className="flex items-center space-x-3">
                      <span className={`text-sm font-medium transition-colors ${isMobile ? 'text-teal-600' : 'text-gray-600'}`}>
                        Mobile
                      </span>
                      <button
                        onClick={() => setIsMobile(!isMobile)}
                        className="relative w-12 h-6 bg-teal-600 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
                      >
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform duration-200 ${isMobile ? 'left-1' : 'left-7'}`}></div>
                      </button>
                      <span className={`text-sm font-medium transition-colors ${!isMobile ? 'text-teal-600' : 'text-gray-600'}`}>
                        Desktop
                      </span>
                    </div>
                  </div>
                </div>
              </div>
           
            <div className="relative animate-fade-in" style={{ animationDelay: '0.4s' }}>
              
              {/* Mobile Mockup */}
              {isMobile && (
                <div className="mx-auto max-w-xs">
                  {/* iPhone mockup */}
                  <div className="relative bg-gray-200 rounded-[2.5rem] p-2 shadow-2xl">
                    {/* Status bar area */}
                    <div className="bg-white rounded-[2rem] overflow-hidden">
                      {/* Status bar */}
                      <div className="bg-white px-6 py-2 flex justify-between items-center text-black text-sm font-medium">
                        <span>9:41</span>
                        <div className="flex items-center space-x-1">
                          <div className="flex space-x-1">
                            <div className="w-1 h-1 bg-black rounded-full"></div>
                            <div className="w-1 h-1 bg-black rounded-full"></div>
                            <div className="w-1 h-1 bg-black rounded-full"></div>
                            <div className="w-1 h-1 bg-black rounded-full"></div>
                          </div>
                          <div className="w-6 h-3 border border-black rounded-sm relative">
                            <div className="w-4/5 h-full bg-green-500 rounded-sm"></div>
                          </div>
                        </div>
                      </div>

                      {/* Browser bar */}
                      <div className="bg-gray-100 px-4 py-3 flex items-center space-x-3">
                        <div className="flex-1 bg-white rounded-lg px-3 py-2 text-sm text-gray-600 flex items-center justify-center">
                          zakat-calculator.com
                        </div>
                      </div>

                      {/* Content area */}
                      <div className="p-4 bg-gray-50 h-[400px] overflow-y-auto hide-scrollbar">
                        {/* Zakat Calculator - Full screen on mobile */}
                        <div className="space-y-3">
                    <div className="h-3 bg-gray-200 rounded w-full"></div>
                    <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                  </div>
                  
                  <div className="mb-6">
                  <div className="relative group transform transition-all duration-300 rounded-lg overflow-hidden">
                          <ZakatCalculatorFlow isDesktop={false} />
                        </div>
                        </div>
                        <div className="space-y-3">
                          <div className="h-3 bg-gray-200 rounded w-full"></div>
                          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                          <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                        </div>
                        <div className="space-y-3 mt-2">
                          <div className="h-3 bg-gray-200 rounded w-full"></div>
                          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                          <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                        </div>
                        <div className="space-y-3 mt-2">
                          <div className="h-3 bg-gray-200 rounded w-full"></div>
                          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                          <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                        </div>
                        <div className="space-y-3 mt-2">
                          <div className="h-3 bg-gray-200 rounded w-full"></div>
                          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                          <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Desktop Mockup */}
              {!isMobile && (
                <div className="w-full max-w-4xl mx-auto hidden lg:block">
                  {/* Desktop Screen Frame */}
                  <div className="bg-gray-200 rounded-xl p-3 shadow-2xl">
                    {/* Screen */}
                    <div className="bg-white rounded-lg overflow-hidden shadow-inner" style={{ aspectRatio: '16/10' }}>
                      {/* macOS-style window header */}
                      <div className="bg-gray-100 px-4 py-3 flex items-center border-b border-gray-200">
                        <div className="flex space-x-2">
                          <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                          <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                          <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                        </div>
                        <div className="flex-1 mx-2">
                          <div className="bg-white rounded-md px-2 py-1 text-sm text-gray-600 border shadow-sm max-w-md">
                            🔒 zakat-calculator.com
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <div className="w-4 h-4 bg-gray-300 rounded"></div>
                          <div className="w-4 h-4 bg-gray-300 rounded"></div>
                        </div>
                      </div>

                      {/* Browser content area */}
                      <div className="bg-gray-50 h-[400px] overflow-y-auto hide-scrollbar">
                        {/* Zakat Calculator centered on desktop */}
                        <div className="mb-2">
                            <div className="h-4 bg-gray-200 rounded w-5/6 mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6 mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6 mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6 mb-2"></div>

                          </div>
                        <div className="flex justify-center h-full">
                          <div className="w-full max-w-sm h-full">
                            <ZakatCalculatorFlow isDesktop={true} />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-6 mb-6 mt-2">
                            <div className="space-y-3">
                              <div className="h-4 bg-gray-200 rounded w-full"></div>
                              <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                              <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                            </div>
                            <div className="space-y-3 mt-2">
                              <div className="h-4 bg-gray-200 rounded w-full"></div>
                              <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                              <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                            </div>
                          </div>

                          {/* Sidebar placeholders */}
                          <div className="grid grid-cols-3 gap-4">
                            <div className="h-24 bg-gray-200 rounded"></div>
                            <div className="h-24 bg-gray-200 rounded"></div>
                            <div className="h-24 bg-gray-200 rounded"></div>
                          </div>
                          <div className="grid grid-cols-2 gap-6 mb-6">
                          <div className="space-y-3">
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                            <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                          </div>
                          <div className="space-y-3">
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                            <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                          </div>
                        </div>

                        {/* Sidebar placeholders */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="h-24 bg-gray-200 rounded"></div>
                          <div className="h-24 bg-gray-200 rounded"></div>
                          <div className="h-24 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Monitor stand */}
                  <div className="flex justify-center">
                    <div className="w-20 h-4 bg-gray-200 rounded-b-lg"></div>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-32 h-2 bg-gray-300 rounded-full"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-slide-up {
          animation: slide-up 0.8s ease-out;
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
      `}</style>
    </div>
    
         <div className="max-w-7xl mx-auto px-4 py-10">
                            <h1 className="text-3xl font-bold text-center text-gray-800 mb-10">Discover More Ads</h1>
                      
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                              {/* Card 1 */}
                              <Link to="/gamification/samsung" className="block group rounded-xl overflow-hidden shadow-lg transition-transform duration-300 transform hover:scale-105 hover:shadow-2xl">
                                <img
                                  src="/assets/sama.jpg"
                                  alt="Card 1"
                                  className="w-full h-60 object-cover transition-opacity duration-300 group-hover:opacity-80"
                                />
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                                  <h2 className="text-lg font-semibold">Samsung</h2>
                                </div>
                              </Link>
                      
                              {/* Card 2 */}
                              <Link to="/video/mollers" className="block group rounded-xl overflow-hidden shadow-lg transition-transform duration-300 transform hover:scale-105 hover:shadow-2xl">
                                <img
                                  src="/assets/moll.avif"
                                  alt="Card 2"
                                  className="w-full h-60 object-cover transition-opacity duration-300 group-hover:opacity-80"
                                />
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                                  <h2 className="text-lg font-semibold">Mollers</h2>
                                </div>
                              </Link>
                      
                              {/* Card 3 */}
                              <Link to="/video/tiger" className="block group rounded-xl overflow-hidden shadow-lg transition-transform duration-300 transform hover:scale-105 hover:shadow-2xl">
                                <img
                                  src="/assets/tiger.avif"
                                  alt="Card 3"
                                  className="w-full h-60 object-cover transition-opacity duration-300 group-hover:opacity-80"
                                />
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                                  <h2 className="text-lg font-semibold">Tiger</h2>
                                </div>
                              </Link>
                      
                              {/* Card 4 */}
                              <Link to="/drag-to-reveal/abcbeer" className="block group rounded-xl overflow-hidden shadow-lg transition-transform duration-300 transform hover:scale-105 hover:shadow-2xl">
                                <img
                                  src="/assets/ab.avif"
                                  alt="Card 4"
                                  className="w-full h-60 object-cover transition-opacity duration-300 group-hover:opacity-80"
                                />
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                                  <h2 className="text-lg font-semibold">ABC Beer</h2>
                                </div>
                              </Link>
                      
                             
                            </div>
                          </div>
    </>
  );
}

export default App;




// import React, { useState, useEffect, useCallback } from 'react';

// const ZakatCalculatorFlow = () => {
//     const [currentStep, setCurrentStep] = useState(1);
//     const [totalAssets, setTotalAssets] = useState('');
//     const [zakatRate, setZakatRate] = useState('2.5');
//     const [nisabThreshold, setNisabThreshold] = useState('85000000');
//     const [calculatedZakat, setCalculatedZakat] = useState(0);
  
//     const handleClickHere = () => setCurrentStep(2);
  
//     const handleCalculateZakat = useCallback(() => {
//       const assets = parseFloat(totalAssets);
//       const threshold = parseFloat(nisabThreshold);
//       const rate = parseFloat(zakatRate) / 100;
//       setCalculatedZakat(assets >= threshold ? assets * rate : 0);
//       setCurrentStep(3);
//     }, [totalAssets, nisabThreshold, zakatRate]);
  
//     const resetFlow = () => {
//       setCurrentStep(1);
//       setTotalAssets('');
//       setZakatRate('2.5');
//       setNisabThreshold('85000000');
//       setCalculatedZakat(0);
//     };
  
//     const backToCalculator = () => setCurrentStep(2);
  

  
// const LandingScreen = () => (
//     <div className="min-h-screen bg-gradient-to-br from-teal-800 via-teal-900 to-green-900 flex flex-col relative overflow-hidden">
//       {/* Background Pattern - Islamic decorative elements */}

//       <div 
//         className="absolute inset-0 opacity-30"
//         style={{
//           backgroundImage: `url('/assets/zakat.webp')`,
//           backgroundSize: 'cover',
//           backgroundPosition: 'center'
//         }}
//       ></div>
     

//       {/* Header */}
//       <div className="relative z-10 pt-16 pb-4 px-6">
//         <div className="text-center mb-2">
//           <h1 className="text-white text-3xl font-bold mb-2">Calculate Your Zakat!</h1>
//           <div className="text-6xl mb-4">🕌</div>
//         </div>
//       </div>

//       {/* Main Content */}
//       <div className="flex-1 flex flex-col items-center justify-center px-6 relative z-10">
//         <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 max-w-xs w-full text-center">
//           <div className="mb-6">
//             <h2 className="text-white text-lg font-bold mb-4">Fulfill Your Religious Duty this Ramadan</h2>
//             <p className="text-white/90 text-sm mb-4">Calculate your Zakat obligation with our easy-to-use calculator</p>
//           </div>
          
//           {/* Click Here Button */}
//           <button 
//             onClick={handleClickHere}
//             className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-bold py-3 px-8 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200 text-lg w-full mb-6"
//           >
//             Click here! 👆
//           </button>

//           {/* Maybank Logo */}
//           <div className="flex items-center justify-center space-x-2">
//             <div className="w-8 h-8 bg-yellow-500 rounded flex items-center justify-center">
//               <span className="text-black font-bold text-sm">M</span>
//             </div>
//             <span className="text-white font-bold text-lg">Maybank</span>
//           </div>
//         </div>
//       </div>

//       {/* Bottom Text */}
//       <div className="relative z-10 pb-12 px-6">
//         <p className="text-white/80 text-center text-sm">
//           Barakallahu feeki - May Allah bless your wealth
//         </p>
//       </div>
//     </div>
//   );
//   const ResultsScreen = () => (

// <div className="min-h-screen bg-gradient-to-br from-teal-800 via-teal-900 to-green-900 flex flex-col items-center justify-center px-6 relative overflow-hidden">
// <div 
//       className="absolute inset-0 opacity-30"
//       style={{
//         backgroundImage: `url('/assets/zakat.webp')`,
//         backgroundSize: 'cover',
//         backgroundPosition: 'center'
//       }}
//     ></div>
//     <div className="absolute inset-0 opacity-20">
//       <div className="absolute top-10 left-10 text-yellow-400 text-3xl">🌙</div>
//       <div className="absolute top-20 right-20 text-yellow-400 text-2xl">⭐</div>
//       <div className="absolute bottom-20 left-20 text-yellow-400 text-2xl">✨</div>
//       <div className="absolute bottom-10 right-10 text-yellow-400 text-3xl">🕌</div>
//     </div>

//     {/* Results */}
//     <div className="text-center relative z-10">
//       <div className="text-4xl mb-2 animate-pulse mt-4">📊</div>
//       <h2 className="text-white text-2xl font-bold mb-2">Zakat Calculation Result</h2>
      
//       <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-8 border border-white/30 max-w-sm w-full">
//         <div className="space-y-4">
//           {/* <div className="text-left">
//             <p className="text-white/90 text-sm mb-1">Total Assets:</p>
//             <p className="text-white text-lg font-bold">IDR {totalAssets ? parseFloat(totalAssets).toLocaleString() : '0'}</p>
//           </div>
          
//           <div className="text-left">
//             <p className="text-white/90 text-sm mb-1">Zakat Rate:</p>
//             <p className="text-white text-lg font-bold">{zakatRate}%</p>
//           </div>
          
//           <div className="text-left">
//             <p className="text-white/90 text-sm mb-1">Nisab Threshold:</p>
//             <p className="text-white text-lg font-bold">IDR {nisabThreshold ? parseFloat(nisabThreshold).toLocaleString() : '0'}</p>
//           </div> */}
          
//           <div className="border-t border-white/30 pt-2">
//             <p className="text-white/90 text-sm mb-1">Your Zakat Obligation:</p>
//             <p className="text-yellow-400 text-2xl font-bold">
//               IDR {calculatedZakat.toLocaleString()}
//             </p>
//             {calculatedZakat === 0 && totalAssets && (
//               <p className="text-white/80 text-xs mt-2">
//                 Your assets are below the nisab threshold, so no zakat is required.
//               </p>
//             )}
//           </div>
//         </div>
//         <div className="mt-4 space-x-2">
//         <button onClick={backToCalculator} className="bg-white text-black px-4 py-2 rounded-lg">Recalculate</button>
//         <button onClick={resetFlow} className=" mt-2 bg-yellow-500 text-black px-4 py-2 rounded-lg">New Calculation</button>
//       </div>
//       </div>

//     </div>

//     {/* Maybank Logo */}
//     <div className="flex items-center justify-center space-x-2 mt-4 relative z-10">
//       <div className="w-8 h-8 bg-yellow-500 rounded flex items-center justify-center">
//         <span className="text-black font-bold text-sm">M</span>
//       </div>
//       <span className="text-white font-bold text-lg">Maybank</span>
//     </div>
//   </div>
//   );

//   return (
//     <>
//       {currentStep === 1 && <LandingScreen />}
//       {currentStep === 2 && (
//         <CalculatorForm
//           totalAssets={totalAssets}
//           setTotalAssets={setTotalAssets}
//           zakatRate={zakatRate}
//           setZakatRate={setZakatRate}
//           nisabThreshold={nisabThreshold}
//           setNisabThreshold={setNisabThreshold}
//           handleCalculateZakat={handleCalculateZakat}
//           resetFlow={resetFlow}
//         />
//       )}
//       {currentStep === 3 && <ResultsScreen />}
//     </>
//   );
// };

//   // Step 2: Zakat Calculator Form
//   const CalculatorForm = React.memo(({ 
//     totalAssets,
//     setTotalAssets,
//     zakatRate,
//     setZakatRate,
//     nisabThreshold,
//     setNisabThreshold,
//     handleCalculateZakat,
//     resetFlow
//   }) => {
//     console.log("CalculatorForm rendered");
//     return (
      // <div className="min-h-screen bg-gradient-to-br from-teal-800 via-teal-900 to-green-900 flex flex-col relative overflow-hidden">
      //   {/* <div 
      //     className="absolute inset-0 opacity-30"
      //     style={{
      //       backgroundImage: url('/assets/zakat.webp'),
      //       backgroundSize: 'cover',
      //       backgroundPosition: 'center'
      //     }}
      //   ></div> */}
//    <div 
//         className="absolute inset-0 opacity-30"
//         style={{
//           backgroundImage: `url('/assets/zakat.webp')`,
//           backgroundSize: 'cover',
//           backgroundPosition: 'center'
//         }}
//       ></div>
//         <div className="relative z-10 pt-10 pb-2 px-6">
//           <div className="flex items-center justify-between mb-4">
//             <button onClick={resetFlow} className="text-white text-2xl w-8 h-8 flex items-center justify-center">❮</button>
//             <div className="text-center">
//               <h1 className="text-white text-2xl font-bold">Calculate Your Zakat!</h1>
//               <div className="text-3xl">🕌</div>
//             </div>
//             <div className="w-4"></div>
//           </div>
//         </div>
  
//         <div className="flex-1 flex flex-col items-center justify-center px-6 relative z-10">
//           <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 max-w-xs w-full">
//             <div className="space-y-4">
//               <div>
//                 <label className="text-white text-sm font-semibold mb-2 block">Total Assets:</label>
//                 <input
//                   type="text"
//                   inputMode="numeric"
//                   value={totalAssets}
//                   onChange={(e) => {
//                     const value = e.target.value;
//                     if (/^\d*$/.test(value)) setTotalAssets(value);
//                   }}
//                   className="w-full px-4 py-2 rounded-xl bg-white/90 border border-white/30 text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400"
//                   placeholder="Enter total assets (IDR)"
//                 />
//               </div>
  
//               <div>
//                 <label className="text-white text-sm font-semibold mb-2 block">Zakat Rate:</label>
//                 <select
//                   value={zakatRate}
//                   onChange={(e) => setZakatRate(e.target.value)}
//                   className="w-full px-4 py-2 rounded-xl bg-white/90 border border-white/30 text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400"
//                 >
//                   <option value="2.5">2.5% (Standard Rate)</option>
//                   <option value="5">5% (Agricultural - Rain Fed)</option>
//                   <option value="10">10% (Agricultural - Irrigated)</option>
//                   <option value="20">20% (Treasure Found)</option>
//                 </select>
//               </div>
  
//               <div>
//                 <label className="text-white text-sm font-semibold mb-2 block">Nisab Threshold:</label>
//                 <input
//                   type="text"
//                   inputMode="numeric"
//                   value={nisabThreshold}
//                   onChange={(e) => {
//                     const value = e.target.value;
//                     if (/^\d*$/.test(value)) setNisabThreshold(value);
//                   }}
//                   className="w-full px-4 py-2 rounded-xl bg-white/90 border border-white/30 text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400"
//                   placeholder="Nisab threshold (IDR)"
//                 />
//                 <p className="text-white/70 text-xs mt-1">Current nisab threshold approximately IDR 85,000,000</p>
//               </div>
  
//               <button
//                 onClick={handleCalculateZakat}
//                 className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-bold py-2 px-8 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200 text-lg w-full"
//               >
//                 Calculate 👆
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   });

// const PhoneMockup = () => {
//   return (
//     <div className="relative group">
//       <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-[3rem] p-3 shadow-2xl border border-slate-600 group-hover:shadow-3xl transition-shadow duration-300">
//         <div className="bg-black rounded-[2.5rem] p-1">
//           <div className="w-[300px] h-[640px] bg-white rounded-[2rem] overflow-hidden relative">
//             {/* Status Bar */}
//             <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-black/50 to-transparent z-20 flex justify-between items-center px-6 pt-2">
//               <span className="text-white text-sm font-medium">11:47</span>
//               <div className="flex items-center space-x-1">
//                 <div className="flex space-x-1">
//                   <div className="w-1 h-1 bg-white rounded-full"></div>
//                   <div className="w-1 h-1 bg-white rounded-full"></div>
//                   <div className="w-1 h-1 bg-white rounded-full"></div>
//                   <div className="w-1 h-1 bg-white rounded-full"></div>
//                 </div>
//                 <span className="text-white text-xs">📶</span>
//                 <span className="text-white text-xs">📱</span>
//                 <span className="text-white text-xs">🔋</span>
//               </div>
//             </div>
            
//             <div className="w-full h-full">
//               <ZakatCalculatorFlow isDesktop={false} />
//             </div>
//           </div>
//         </div>
        
//         {/* Home Indicator */}
//         <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-slate-500 rounded-full"></div>
//       </div>
//     </div>
//   );
// };

// const DesktopMockup = () => {
//   return (
//     <div className="relative group">
//       <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 shadow-2xl border border-slate-600 group-hover:shadow-3xl transition-shadow duration-300">
//         <div className="bg-black rounded-xl p-3">
//           <div className="w-[800px] h-[700px] bg-white rounded-lg overflow-hidden relative">
//             {/* Browser Header */}
//             <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-slate-100 to-slate-50 border-b border-slate-200 flex items-center px-6 z-20">
//               <div className="flex space-x-2">
//                 <div className="w-3 h-3 bg-red-500 rounded-full"></div>
//                 <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
//                 <div className="w-3 h-3 bg-green-500 rounded-full"></div>
//               </div>
//               <div className="flex-1 mx-6">
//                 <div className="bg-white rounded-lg px-4 py-2 text-sm text-slate-600 border border-slate-200 shadow-sm">
//                   https://maybank.com/zakat-calculator
//                 </div>
//               </div>
//             </div>
            
//             <div className="w-full h-full pt-12">
//               <ZakatCalculatorFlow isDesktop={true} />
//             </div>
//           </div>
//         </div>
        
//         {/* Desktop Stand */}
//         <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-6">
//           <div className="w-40 h-8 bg-gradient-to-b from-slate-600 to-slate-800 rounded-b-xl"></div>
//           <div className="w-56 h-4 bg-gradient-to-b from-slate-700 to-slate-900 rounded-full mx-auto shadow-lg"></div>
//         </div>
//       </div>
//     </div>
//   );
// };

// const ResponsiveZakatCalculatorFlow = () => {
//   return (
//     <div className="mt-14 min-h-screen bg-gradient-to-br from-slate-100 via-slate-200 to-slate-300 p-8">
//       {/* Header */}
//       <div className="text-center mb-12">
//         <h1 className="text-4xl font-bold text-slate-800 mb-4">Maybank Zakat Calculator</h1>
//         <p className="text-slate-600 text-lg">Calculate your Zakat obligation with precision and ease</p>
//       </div>
      
//       {/* Mobile View */}
//       <div className="lg:hidden flex items-center justify-center min-h-screen">
//         <div className="text-center space-y-6">
//           <div>
//             <h3 className="text-slate-800 text-2xl font-bold">Mobile Experience</h3>
//             <p className="text-slate-600 text-sm mt-2">Touch-optimized Zakat calculator with Islamic design elements</p>
//           </div>
//           <PhoneMockup />
//         </div>
//       </div>
      
//       {/* Desktop View - Both Mockups */}
//       <div className="hidden lg:flex lg:flex-row lg:space-x-16 lg:items-center lg:justify-center lg:py-12">
//         <div className="flex flex-col items-center space-y-6">
//           <div className="text-center">
//             <h3 className="text-slate-800 text-2xl font-bold">Desktop Experience</h3>
//             <p className="text-slate-600 text-sm mt-2">Comprehensive Zakat calculator with detailed calculations</p>
//           </div>
//           <DesktopMockup />
//         </div>
        
//         <div className="flex flex-col items-center space-y-6">
//           <div className="text-center">
//             <h3 className="text-slate-800 text-2xl font-bold">Mobile Experience</h3>
//             <p className="text-slate-600 text-sm mt-2">Optimized for mobile Islamic banking</p>
//           </div>
//           <PhoneMockup />
//         </div>
//       </div>

//       {/* Flow Description */}
//       <div className="mt-16 max-w-4xl mx-auto bg-white rounded-2xl p-8 shadow-lg">
//         <h2 className="text-2xl font-bold text-slate-800 mb-6">Zakat Calculator Flow</h2>
//         <div className="grid md:grid-cols-3 gap-6">
//           <div className="text-center">
//             <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
//               <span className="text-2xl">🕌</span>
//             </div>
//             <h3 className="font-semibold text-slate-800 mb-2">Step 1: Welcome</h3>
//             <p className="text-slate-600 text-sm">Islamic-themed welcome screen encouraging Zakat calculation</p>
//           </div>
//           <div className="text-center">
//             <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
//               <span className="text-2xl">🧮</span>
//             </div>
//             <h3 className="font-semibold text-slate-800 mb-2">Step 2: Calculator</h3>
//             <p className="text-slate-600 text-sm">Enter assets, select rate, and set nisab threshold</p>
//           </div>
//           <div className="text-center">
//             <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
//               <span className="text-2xl">📊</span>
//             </div>
//             <h3 className="font-semibold text-slate-800 mb-2">Step 3: Results</h3>
//             <p className="text-slate-600 text-sm">Display calculated Zakat amount with detailed breakdown</p>
//           </div>
//         </div>
        
//         <div className="mt-8 p-6 bg-teal-50 rounded-xl">
//           <h3 className="font-semibold text-slate-800 mb-3">Key Features</h3>
//           <ul className="text-slate-600 text-sm space-y-2">
//             <li>• Accurate Zakat calculation based on Islamic principles</li>
//             <li>• Multiple Zakat rates for different types of wealth</li>
//             <li>• Nisab threshold verification</li>
//             <li>• Beautiful Islamic-inspired design</li>
//             <li>• Mobile-optimized interface</li>
//             <li>• Maybank branding integration</li>
//           </ul>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ResponsiveZakatCalculatorFlow;


